/**
 * Capture a DOM element (a printable document) as a JPG image and share it
 * as a real file — so it arrives as an image in WhatsApp / Email / etc.,
 * instead of sharing the app link.
 *
 * Falls back to downloading the JPG when the device can't share files.
 */
import { domToBlob } from 'modern-screenshot';

/** Render an element to a JPEG Blob (browser-native rendering → correct Arabic). */
async function elementToJpegBlob(el) {
  return await domToBlob(el, {
    type: 'image/jpeg',
    quality: 0.95,
    backgroundColor: '#ffffff',
    scale: 2,                 // crisp text
  });
}

/**
 * Capture an element to a JPEG blob. If `captureWidth` is given, the element is
 * rendered at that fixed pixel width (via an off-screen clone) so the image
 * isn't squished to the phone's viewport width — e.g. a true A4 sheet shares as
 * a wide A4 image even from a narrow mobile screen. The on-screen element and
 * the layout are never touched (no visible flash).
 */
async function captureBlob(el, captureWidth) {
  if (!captureWidth) return elementToJpegBlob(el);

  const clone = el.cloneNode(true);
  clone.style.width = `${captureWidth}px`;
  clone.style.maxWidth = `${captureWidth}px`;
  clone.style.zoom = 'normal';
  clone.style.position = 'fixed';
  clone.style.left = '-100000px';
  clone.style.top = '0';
  clone.style.margin = '0';
  clone.style.transform = 'none';
  document.body.appendChild(clone);
  try {
    // Let the clone lay out at the forced width before capturing.
    void clone.offsetWidth;
    return await elementToJpegBlob(clone);
  } finally {
    clone.remove();
  }
}

/**
 * Share `el` as a JPG file. Returns one of:
 *   'shared'     — opened the native share sheet
 *   'downloaded' — fell back to a file download
 *   'cancelled'  — user dismissed the share sheet
 * Throws on capture failure.
 *
 * Options:
 *   filename     — download/share file name
 *   title        — share-sheet title
 *   captureWidth — force a fixed pixel width for the image (e.g. 800 for A4)
 */
export async function shareElementAsImage(el, { filename = 'document.jpg', title = '', captureWidth } = {}) {
  if (!el) throw new Error('no element to capture');

  const safeName = filename.endsWith('.jpg') ? filename : `${filename}.jpg`;
  const blob = await captureBlob(el, captureWidth);
  if (!blob) throw new Error('capture failed');

  const file = new File([blob], safeName, { type: 'image/jpeg' });

  // Preferred path: share the actual image file.
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: title || safeName });
      return 'shared';
    } catch (err) {
      // AbortError = user cancelled; anything else falls through to download.
      if (err && err.name === 'AbortError') return 'cancelled';
    }
  }

  // Fallback: download the JPG so the user can attach it manually.
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = safeName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return 'downloaded';
}
